<template>
  <section>
    <NotificationCenter
      v-if="isNotificationsShown"
      v-bind="{ errors, notifications, successes, warnings }"
    />
    <LoadingLayout v-if="isAppLoading" />
    <template v-else>
      <MainLayout />
      <ShopFooter v-if="showShopFooter" />
      <PaymentFlowFooter v-else />
    </template>
    <CaptchaBlock
      ref="invisibleRecaptcha"
      keyType="login"
      @captcha-verify-action="setCaptchaToken"
    />
  </section>
</template>

<script>
import { getAccessToken } from '@internetworkexpert/js-common'
import { mapState, mapGetters, mapActions } from 'vuex'
import LoadingLayout from '@/layouts/LoadingLayout.vue'
import MainLayout from '@/layouts/MainLayout.vue'
import NotificationCenter from '@/components/notifications/NotificationCenter.vue'
import CONSTANTS from '@/constants'
import { eventsConstants } from '@/helpers/eventsList'
import CaptchaBlock from '@/components/shared/CaptchaBlock.vue'
import { buildCheckoutTitle, DEFAULT_CHECKOUT_TITLE } from '@/helpers/pageTitle'
import { updateCart, routesRules } from '@/helpers/mixins'
import ShopFooter from '@/components/flow/ShopFooter.vue'
import PaymentFlowFooter from '@/components/flow/PaymentFlowFooter.vue'

export default {
  name: 'App',
  components: {
    LoadingLayout,
    MainLayout,
    CaptchaBlock,
    NotificationCenter,
    ShopFooter,
    PaymentFlowFooter,
  },
  mixins: [updateCart, routesRules],
  data() {
    return {
      captchaToken: '',
    }
  },
  computed: {
    ...mapState({
      notifications: state => state.app.appNotifications,
      errors: state => state.app.appErrors,
      successes: state => state.app.appSuccesses,
      warnings: state => state.app.appWarnings,
      userProfile: state => state.auth.userProfile,
      isAppLoading: state => state.app.isAppLoading,
      isAuthenticated: state => state.auth.isAuthenticated,
      availablePlans: state => state.products.availablePlans,
      isInPartnerFlow: state => state.subscriptions.isInPartnerFlow,
      paymentMethod: state => state.subscriptions.paymentMethod,
      utmParams: state => state.events.utmParams,
      promotionalField: state => state.subscriptions.promotionalField,
      shoppingCart: state => state.shoppingCart.shoppingCart,
      availableCertifications: state => state.products.availableCertifications,
      availableLiveVirtualTrainings: state => state.products.availableLiveVirtualTrainings,
      availableProductBundles: state => state.products.availableProductBundles,
      extensions: state => state.products.extensions,
      userHasSubscription: state => state.subscriptions.userHasSubscription,
    }),
    ...mapGetters({
      isBusinessUser: 'business/isBusinessUser',
      couponStr: 'coupons/getCoupon',
      recurlyPricing: 'recurly/getRecurlyPricing',
      recurlyTokenActionResult: 'recurly/getRecurlyTokenActionResult',
      isRestrictStandAloneCertsEnabled: 'featureFlags/isRestrictStandAloneCertsEnabled',
    }),
    recurlyKey() {
      return process.env.VUE_APP_RECURLY_KEY
    },
    isNotificationsShown() {
      return (
        !!this.errors.length > 0 ||
        !!this.notifications.length > 0 ||
        !!this.successes.length > 0 ||
        !!this.warnings.length > 0
      )
    },
    allAvailableProductsForTitle() {
      const collections = [
        this.availablePlans || [],
        this.availableCertifications || [],
        this.availableProductBundles || [],
        this.availableLiveVirtualTrainings || [],
        this.extensions || [],
      ]
      return collections
        .reduce((acc, current) => acc.concat(current || []), [])
        .filter(item => item && typeof item === 'object')
    },
    productFromCartForTitle() {
      const cart = Array.isArray(this.shoppingCart) ? this.shoppingCart : []
      const priority = [
        'plan',
        'bundle',
        'certificate',
        'live_virtual_training',
        'extension',
        'addon',
      ]
      const match = priority
        .map(type => cart.find(item => item && item.product_type === type))
        .find(item => item)
      if (match) {
        return match
      }
      return cart.length ? cart[0] : null
    },
    productFromQueryForTitle() {
      const query = this.$route && this.$route.query ? this.$route.query : {}
      const rawValues = [query.product_selected, query.products, query.plan].filter(Boolean)
      if (!rawValues.length) {
        return null
      }
      const ids = rawValues
        .map(value => value.split(','))
        .reduce((acc, parts) => acc.concat(parts), [])
        .map(id => (id ? id.trim() : ''))
        .filter(Boolean)
      if (!ids.length) {
        return null
      }
      const lookup = this.allAvailableProductsForTitle.reduce((map, product) => {
        if (product && product.merchant_id && !map.has(product.merchant_id)) {
          map.set(product.merchant_id, product)
        }
        return map
      }, new Map())
      const foundProductId = ids.find(id => lookup.has(id))
      if (foundProductId) {
        return lookup.get(foundProductId)
      }
      return null
    },
    productForTitle() {
      return this.productFromCartForTitle || this.productFromQueryForTitle || null
    },
    pageTitle() {
      return buildCheckoutTitle({
        product: this.productForTitle,
        routeName: this.$route && this.$route.name ? this.$route.name.toString() : null,
      })
    },
  },
  watch: {
    pageTitle: {
      handler(newTitle) {
        if (typeof document !== 'undefined') {
          document.title = newTitle || DEFAULT_CHECKOUT_TITLE
        }
      },
      immediate: true,
    },
    isInPartnerFlow(state) {
      if (state) {
        this.submitPartnerStackInfo()
      }
    },
  },
  methods: {
    ...mapActions({
      initializeDarkly: 'featureFlags/initializeDarkly',
      addCoupon: 'coupons/addCoupon',
      createRecurlyInstance: 'recurly/createRecurlyInstance',
    }),
    async fetchPlaceOrderRecaptcha() {
      await this.$refs.invisibleRecaptcha.execute()
    },
    setCaptchaToken({ recaptcha }) {
      this.captchaToken = recaptcha
    },
    async submitPartnerStackInfo() {
      try {
        if (typeof window === 'undefined') return
        const growsumoInstance = window.growsumo
        if (!growsumoInstance || !growsumoInstance.data) {
          return
        }
        const hasReferralData =
          Boolean(growsumoInstance.data.xid) || Boolean(growsumoInstance.data.partner_key)
        if (!hasReferralData) {
          return
        }
        const { first_name, last_name, email, id } = this.userProfile
        const fullName = [first_name, last_name].filter(Boolean).join(' ').trim() || email
        growsumoInstance.data.name = fullName
        growsumoInstance.data.email = email
        growsumoInstance.data.customer_key = id
        growsumoInstance.createSignup()
      } catch (error) {
        this.$store.dispatch('app/setError', error)
      }
    },
    async handleCouponParam() {
      const params = new URLSearchParams(window.location.search)
      const couponStr = params.get('coupon')
      if (couponStr && couponStr.length) {
        // uncomment this if we want to support multiple coupons
        // couponStr.split(',').forEach(async couponStr => {
        //   await this.addCoupon(couponStr)
        // })
        await this.addCoupon(couponStr)
      }
    },
    handleParams() {
      const { PLANS, VOUCHERS, BLACK_FRIDAY_PRODUCT_BUNDLES } = CONSTANTS
      const params = new URLSearchParams(window.location.search)
      const products = params.get('products') || params.get('product_selected')
      const plan = params.get('plan')
      const subscriptionsViewSelected = params.get('sub_view_selected')
      const excludedPlanIds = [
        PLANS.SKILL_DIVE_ANNUAL,
        BLACK_FRIDAY_PRODUCT_BUNDLES.INE_BLACK_FRIDAY_BUNDLE_SKILL_DIVE,
        BLACK_FRIDAY_PRODUCT_BUNDLES.INE_CYBER_MONDAY_SKILL_DIVE,
      ]
      const excludedBundlesIds = [
        BLACK_FRIDAY_PRODUCT_BUNDLES.INE_BLACK_FRIDAY_BUNDLE_SKILL_DIVE,
        BLACK_FRIDAY_PRODUCT_BUNDLES.INE_CYBER_MONDAY_SKILL_DIVE,
      ]
      const isExcludedPlan = value => !!value && excludedPlanIds.includes(value)
      const isExcludedBundle = value => !!value && excludedBundlesIds.includes(value)
      this.saveUTMVars(params)
      const promoParam = params.get('special_offer')
      if (promoParam && promoParam.length) {
        this.$store.dispatch('subscriptions/setPromotionalField', promoParam)
      }
      const planIsExcluded = isExcludedPlan(plan)
      if (plan && plan.length) {
        const intendedPlan = this.availablePlans.find(p => p.merchant_id === plan)
        if (intendedPlan && !this.userHasSubscription && !planIsExcluded) {
          this.addProductToCart(intendedPlan)
        }
      }
      if (subscriptionsViewSelected && subscriptionsViewSelected.length) {
        this.$store.dispatch('app/setSubscriptionView', subscriptionsViewSelected)
      }
      if (products && products.length) {
        const productsArray = products.split(',')
        const bundleSlugPriority = [
          BLACK_FRIDAY_PRODUCT_BUNDLES.INE_FUND_ANNUAL_1YR,
          BLACK_FRIDAY_PRODUCT_BUNDLES.INE_CYBER_MONDAY_SKILL_DIVE,
        ]
        productsArray.map(i => {
          const trimmedProduct = i.trim()
          const planIsBundle =
            trimmedProduct === PLANS.EJPT_FUNDAMENTALS ||
            trimmedProduct === PLANS.EDA_PREMIUM ||
            trimmedProduct === PLANS.EWPT_PREMIUM
          const prefersBundle = bundleSlugPriority.includes(trimmedProduct)
          const productIsExcludedPlan =
            isExcludedPlan(trimmedProduct) || isExcludedBundle(trimmedProduct)
          const foundPlan = this.availablePlans.find(
            p => p.merchant_id === trimmedProduct && !planIsBundle
          )
          const foundBundle = this.availablePlans.find(
            p => p.merchant_id === trimmedProduct && planIsBundle
          )
          const foundProductBundle = this.availableProductBundles.find(
            p => p?.merchant_id === trimmedProduct || p?.slug === trimmedProduct
          )
          const foundCertification = this.availableCertifications.find(
            p => p.merchant_id === trimmedProduct
          )
          const foundLiveVirtualTraining = this.availableLiveVirtualTrainings.find(
            p => p.merchant_id === trimmedProduct
          )
          const shouldSkipPlanSelection =
            productIsExcludedPlan || (prefersBundle && !!foundProductBundle)
          if (foundPlan) {
            if (
              !this.shoppingCart.includes(foundPlan) &&
              !this.userHasSubscription &&
              !this.isBusinessUser &&
              !shouldSkipPlanSelection
            ) {
              this.updateSelectedPlan(foundPlan)
              this.handleRedirection()
            }
          }
          if (foundBundle) {
            if (
              !this.shoppingCart.includes(foundBundle) &&
              !this.userHasSubscription &&
              !this.isBusinessUser &&
              !shouldSkipPlanSelection
            ) {
              this.updateSelectedPlan(foundBundle)
              this.handleRedirection()
            }
          }
          if (foundProductBundle) {
            const bundleAlreadyInCart = this.shoppingCart.some(
              product => product?.slug === foundProductBundle.slug
            )
            if (
              !bundleAlreadyInCart &&
              !this.userHasSubscription &&
              !this.isBusinessUser &&
              !shouldSkipPlanSelection
            ) {
              this.updateSelectedBundle(foundProductBundle)
              this.handleRedirection()
            }
          }
          if (foundCertification) {
            if (!this.shoppingCart.includes(foundCertification)) {
              if (
                [VOUCHERS.EWPT, VOUCHERS.EDA, VOUCHERS.EJPT].includes(
                  foundCertification.merchant_id
                ) &&
                this.userHasSubscription &&
                this.isRestrictStandAloneCertsEnabled
              ) {
                this.updateSelectedPlan(foundCertification)
                this.handleRedirection()
              } else if (
                Object.values(VOUCHERS).includes(foundCertification.merchant_id) &&
                ![VOUCHERS.EWPT, VOUCHERS.EDA, VOUCHERS.EJPT].includes(
                  foundCertification.merchant_id
                )
              ) {
                this.updateSelectedCertification(foundCertification)
                this.handleRedirection()
              } else {
                this.$router.push({ name: 'shop' })
              }
            }
          }
          if (foundLiveVirtualTraining) {
            if (!this.shoppingCart.includes(foundLiveVirtualTraining)) {
              this.addProductToCart(foundLiveVirtualTraining)
              if (this.isAuthenticated) {
                this.$router.push({ name: 'payment' })
              } else {
                this.$router.push({ name: 'account' })
              }
            }
          }
        })
      }
    },
    handleRedirection() {
      const params = new URLSearchParams(window.location.search)
      if (params.get('product_selected')) {
        if (this.isAuthenticated) {
          this.$router.push({ name: 'payment' })
        } else {
          this.$router.push({ name: 'account' })
        }
      }
    },
    saveUTMVars(params) {
      const payload = {}
      CONSTANTS.UTM_VARS.forEach(key => {
        if (params.has(key)) {
          payload[key] = params.get(key)
        }
      })

      if (typeof window !== 'undefined') {
        try {
          const gclid = params.get('gclid')
          if (gclid) {
            window.sessionStorage?.setItem('gclid', gclid)
          }
          const fbclid = params.get('fbclid')
          if (fbclid) {
            window.sessionStorage?.setItem('fbclid', fbclid)
          }
        } catch {
          // Ignore sessionStorage access errors
        }
      }

      this.$store.commit('events/UPDATE_UTM_PARAMS', payload)
    },
    sendCheckoutStartedEvent() {
      this.sendMixpanelEvent(eventsConstants.CHECKOUT_STARTED, {
        coupon: this.couponStr,
        available_plans: `${this.availablePlans.map(p => p.plan_code)}`,
        special_offer: this.promotionalField,
        ...this.utmParams,
      })
    },
  },
  async created() {
    this.$store.dispatch('app/isAppLoading', true)
    try {
      const token = getAccessToken()
      this.createRecurlyInstance()
      if (token && token.length) {
        await this.$store.dispatch('auth/attemptLogin', token)
      }
      if (this.isAuthenticated) {
        await this.$store.dispatch('subscriptions/getCurrentPaymentMethodOnFile')
        await this.$store.dispatch('business/getBusinessUser')
      }
      await this.initializeDarkly()
      await this.handleCouponParam()
      await this.$store.dispatch('products/fetchAvailableProducts')
      await this.$store.dispatch('products/fetchAvailableProductBundles')
      await this.$store.dispatch('products/getPassTypes')
      this.handleParams()
      if (this.isAuthenticated) {
        this.sendCheckoutStartedEvent()
      }
    } catch (error) {
      this.$store.dispatch('app/setError', error)
    }
    this.$store.dispatch('app/isAppLoading', false)
  },
  mounted() {
    window.recurly.configure(this.recurlyKey)
  },
}
</script>

<style lang="scss">
// Account for notches and statusbars
body {
  padding-top: constant(safe-area-inset-top);
  padding-top: env(safe-area-inset-top);
  padding-bottom: constant(safe-area-inset-bottom);
  padding-bottom: env(safe-area-inset-bottom);
  padding-left: constant(safe-area-inset-left);
  padding-left: env(safe-area-inset-left);
  padding-right: constant(safe-area-inset-right);
  padding-right: env(safe-area-inset-right);
  scroll-behavior: smooth;
}
@import './src/styles/app.scss';
@import '~vue-loaders/dist/vue-loaders.css';
$material-icons-font-path: '~material-icons/iconfont/';
@import '~material-icons/iconfont/material-icons.scss';

html {
  scroll-behavior: smooth;
}

* {
  -webkit-font-smoothing: antialiased;
}

.container {
  max-width: 100%;
  min-height: 150vh;
  background: var(--main-bg-color);
  background-image: url('./assets/images/backgrounds/main_bg.jpg');
  background-position: inherit;
  background-repeat: no-repeat;
  background-size: cover;
  margin: 0 auto;
  padding: 0 0 50px 0;
  @include tablet {
    min-height: 100vh;
  }
  &__mask {
    position: absolute;
    height: 100%;
    width: 100%;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    background: rgba(0, 0, 0, 0.8);
  }
}
#recurly-3ds {
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  height: 98%;
  width: 98%;
  margin: auto;
  z-index: 9999;
}
.fade-enter-active,
.fade-leave-active {
  transition-duration: 0.2s;
  transition-property: height, opacity;
  transition-timing-function: ease;
  overflow: hidden;
}
.fade-enter,
.fade-leave-active {
  opacity: 0;
}
</style>
